import { useParams, Link } from 'react-router-dom';
import { getComicById, getRelatedComics } from '@/mocks/comics';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function ComicPage() {
  const { id } = useParams<{ id: string }>();
  const comic = getComicById(id || '');

  if (!comic) {
    return (
      <>
        <SEOHead title="漫画未找到 - 布丁漫画" description="抱歉，您查找的漫画不存在或已被移除。" />
        <Navbar />
        <main className="pt-16 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <span className="w-16 h-16 flex items-center justify-center mx-auto mb-4 text-foreground-300">
              <i className="ri-emotion-sad-line text-4xl"></i>
            </span>
            <h2 className="text-xl font-heading font-bold text-foreground-950 mb-2">漫画未找到</h2>
            <p className="text-sm text-foreground-500 mb-6">抱歉，您查找的漫画不存在或已被移除。</p>
            <Link
              to="/"
              className="px-6 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap inline-flex items-center gap-1"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedComics = getRelatedComics(comic);
  const fakeChapters = Array.from({ length: Math.min(comic.chapters, 20) }, (_, i) => ({
    number: comic.chapters - i,
    title: `第${comic.chapters - i}话`,
    date: new Date(Date.now() - i * 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  }));

  const articleSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: `${comic.title} - 布丁漫画下拉式免费阅读`,
        description: comic.description,
        author: {
          '@type': 'Person',
          name: comic.author,
        },
        datePublished: '2019-01-01',
        dateModified: comic.updatedAt,
        image: comic.coverImage,
        publisher: {
          '@type': 'Organization',
          name: '布丁漫画',
        },
      },
      {
        '@type': 'WebPage',
        name: `${comic.title} - 布丁漫画`,
        url: `https://budingmanhua.com/comic/${comic.id}`,
        isPartOf: { '@id': 'https://budingmanhua.com/#website' },
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${comic.title} - 布丁漫画下拉式免费阅读 | 布丁漫画`}
        description={`${comic.title} - ${comic.description.slice(0, 120)}。布丁漫画下拉式免费阅读，在线追更全集。`}
        keywords={`布丁漫画下拉式看,${comic.title},${comic.category},下拉式阅读,免费漫画,全集漫画,在线追更`}
        canonicalUrl={`https://budingmanhua.com/comic/${comic.id}`}
        ogType="article"
        ogImage={comic.coverImage}
        schema={articleSchema}
      />
      <Navbar />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: comic.category, href: `/category/${comic.categorySlug}` },
              { label: comic.title },
            ]}
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 pb-10">
          <div className="flex flex-col lg:flex-row gap-8 md:gap-10">
            <div className="flex-shrink-0 w-full sm:w-64 mx-auto lg:mx-0">
              <div className="aspect-[5/7] rounded-xl overflow-hidden bg-background-100">
                <img
                  src={comic.coverImage}
                  alt={`${comic.title} 封面 - 布丁漫画下拉式漫画`}
                  title={`${comic.title} | 布丁漫画下拉式免费阅读`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-1">
                {comic.title}
              </h1>
              <p className="text-sm text-foreground-500 mb-4">{comic.author} · 著</p>

              <div className="flex flex-wrap items-center gap-2 mb-4">
                <span className="px-3 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-full">
                  {comic.category}
                </span>
                {comic.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 text-xs bg-secondary-100 text-secondary-700 rounded-full">
                    {tag}
                  </span>
                ))}
                {comic.status === '连载中' ? (
                  <span className="px-3 py-1 text-xs font-medium bg-accent-100 text-accent-700 rounded-full">
                    连载中
                  </span>
                ) : (
                  <span className="px-3 py-1 text-xs font-medium bg-secondary-200 text-secondary-700 rounded-full">
                    已完结
                  </span>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-4 md:gap-6 mb-5 text-sm">
                <div className="flex items-center gap-1">
                  <span className="w-4 h-4 flex items-center justify-center text-accent-500">
                    <i className="ri-star-fill text-sm"></i>
                  </span>
                  <span className="font-semibold text-foreground-900">{comic.rating}</span>
                  <span className="text-foreground-400">分</span>
                </div>
                <div className="flex items-center gap-1 text-foreground-500">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-eye-line text-sm"></i>
                  </span>
                  <span>{comic.views} 阅读</span>
                </div>
                <div className="flex items-center gap-1 text-foreground-500">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-file-list-3-line text-sm"></i>
                  </span>
                  <span>共 {comic.chapters} 话</span>
                </div>
                <div className="flex items-center gap-1 text-foreground-500">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-time-line text-sm"></i>
                  </span>
                  <span>更新于 {comic.updatedAt}</span>
                </div>
              </div>

              <div className="bg-background-100 rounded-xl p-4 md:p-5 mb-6">
                <h2 className="text-sm font-semibold text-foreground-900 mb-2">作品简介</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
              </div>

              <div className="flex flex-wrap gap-3">
                <Link
                  to={`/comic/${comic.id}`}
                  className="px-8 py-3 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  开始阅读第一话
                  <span className="w-4 h-4 inline-flex items-center justify-center ml-1">
                    <i className="ri-play-line text-sm"></i>
                  </span>
                </Link>
                <button className="px-6 py-3 bg-secondary-100 text-foreground-700 text-sm font-medium rounded-full hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap">
                  <span className="w-4 h-4 inline-flex items-center justify-center mr-1">
                    <i className="ri-bookmark-line text-sm"></i>
                  </span>
                  加入收藏
                </button>
              </div>
            </div>
          </div>

          <div className="mt-10 md:mt-14">
            <h2 className="text-xl font-heading font-bold text-foreground-950 mb-5">章节列表</h2>
            <div className="bg-background-100 rounded-xl border border-background-200/60 overflow-hidden">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-background-200/60">
                {fakeChapters.map((ch) => (
                  <Link
                    key={ch.number}
                    to={`/comic/${comic.id}`}
                    className="flex items-center justify-between px-4 py-3 bg-background-50 hover:bg-primary-50 transition-colors cursor-pointer"
                  >
                    <span className="text-sm text-foreground-800 whitespace-nowrap">{ch.title}</span>
                    <span className="text-xs text-foreground-400 whitespace-nowrap">{ch.date}</span>
                  </Link>
                ))}
              </div>
              {comic.chapters > 20 && (
                <div className="px-4 py-3 text-center border-t border-background-200/60">
                  <span className="text-sm text-foreground-500">还有 {comic.chapters - 20} 话，持续更新中...</span>
                </div>
              )}
            </div>
          </div>

          {relatedComics.length > 0 && (
            <div className="mt-10 md:mt-14">
              <h2 className="text-xl font-heading font-bold text-foreground-950 mb-5">相关推荐</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop="">
                {relatedComics.map((rc) => (
                  <ComicCard key={rc.id} comic={rc} size="small" />
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}